import {
  ACCESS_KINDS,
  parentProjectId,
  type AccessKind,
} from "@mattoboard/schemas";
import type { Permissions } from "ts-interfaces";
import { allFirmIdsForUser } from "ui/utils/team/teamRole";
import { sharingV2Doc } from "ui/utils/workspace/sharingV2";
import type { KindCounts } from "ui/utils/workspace/accessApi";
import { trackEvent, trackEventGA } from "../libs/analyticsManager";

// Team Trends events (docs/team-analytics-events.md). GA4 names all start
// with `team_`; absent `firm_id` means Personal.

type Value = string | number | boolean | null | undefined;
type Props = Record<string, Value>;

export type ActorAccess = "owner" | "editor" | "viewer";
export type Level = "view" | "edit";
export type EveryoneLevel = "none" | Level;

export interface TeamItem {
  item_kind: AccessKind;
  item_id: string;
  firm_id: string | null;
  actor_access?: ActorAccess;
  in_project?: boolean;
  sharing_v2: boolean;
}

// A thunk builds its props inside the try, for props read off a response.
const fire = (name: string, gaName: string, props: Props | (() => Props) = {}) => {
  try {
    const clean = Object.fromEntries(
      Object.entries(typeof props === "function" ? props() : props).filter(
        ([, v]) => v !== null && v !== undefined,
      ),
    );
    trackEvent(`Team - ${name}`, clean);
    trackEventGA(gaName, clean);
  } catch (error) {
    console.error("[teamAnalytics]", error);
  }
};

// Who is signed in and which teams they're an active member of, kept by
// useTeamAnalyticsContext so API-layer call sites need neither.
let ctx: { uid: string | null; firms: Set<string> } = { uid: null, firms: new Set() };
const seen = new Set<string>();

export const setTeamAnalyticsContext = (
  uid: string | null,
  permissions: Permissions | null | undefined,
) => {
  const inactive = new Set(permissions?.teamsInactive ?? []);
  // Once per item per tab is per account: another sign-in starts over.
  if (uid !== ctx.uid) seen.clear();
  ctx = {
    uid,
    firms: new Set(allFirmIdsForUser(permissions).filter((f) => !inactive.has(f))),
  };
};

// The active workspace's team, for a switch's `from`.
let activeFirm: string | null = null;
export const setTeamAnalyticsActiveFirm = (firmId: string | null) => {
  activeFirm = firmId;
};

// A reused invite link isn't a new join: once per account and team in this browser.
const firstJoin = (uid: string | null, firmId: string) => {
  try {
    const key = `teamJoined:${uid ?? ""}:${firmId}`;
    if (localStorage.getItem(key)) return false;
    localStorage.setItem(key, "1");
  } catch {
    // Storage blocked: report it.
  }
  return true;
};

const onceForItem = (key: string) => {
  if (seen.has(key)) return false;
  seen.add(key);
  return true;
};

export const __resetTeamAnalyticsForTests = () => {
  seen.clear();
  ctx = { uid: null, firms: new Set() };
  activeFirm = null;
  createEntry = null;
};

// Any stored item (board, project, …); read loosely.
type Doc = object | null | undefined;
const field = (doc: Doc, key: string): unknown => (doc as Record<string, unknown> | null | undefined)?.[key];

const firmOf = (doc: Doc): string | null => {
  const f = field(doc, "design_firm_id");
  return typeof f === "string" && f !== "" ? f : null;
};

export const ownerOf = (kind: AccessKind, doc: Doc): string | null => {
  const o = field(doc, ACCESS_KINDS[kind].ownerField);
  return typeof o === "string" && o !== "" ? o : null;
};

/** The item params; a non-owner gets `access` (viewer unless given). */
export const teamItem = (
  kind: AccessKind,
  id: string,
  doc: Doc,
  access: Exclude<ActorAccess, "owner"> = "viewer",
  uid: string | null = ctx.uid,
): TeamItem => {
  try {
    return {
      item_kind: kind,
      item_id: id,
      firm_id: firmOf(doc),
      actor_access: uid && ownerOf(kind, doc) === uid ? "owner" : access,
      in_project: !!doc && !!parentProjectId(kind, doc),
      sharing_v2: sharingV2Doc(doc),
    };
  } catch {
    return { item_kind: kind, item_id: id, firm_id: null, sharing_v2: false };
  }
};

/** The item params when the signed-in user is a teammate (not the owner) of a team item. */
export const teammateItem = (
  kind: AccessKind,
  id: string,
  doc: Doc,
  access: Exclude<ActorAccess, "owner"> = "viewer",
): TeamItem | null => {
  try {
    const firm = firmOf(doc);
    const owner = ownerOf(kind, doc);
    if (!ctx.uid || !firm || !owner || owner === ctx.uid || !ctx.firms.has(firm)) return null;
    return teamItem(kind, id, doc, access);
  } catch {
    return null;
  }
};

/** Params when only the team and the owner are known (the legacy share dialog). */
export const looseItem = (
  kind: AccessKind,
  id: string,
  firmId: string | null | undefined,
  owner: string | null | undefined,
  sharingV2 = false,
): TeamItem => ({
  item_kind: kind,
  item_id: id,
  firm_id: firmId || null,
  actor_access: !owner ? undefined : owner === ctx.uid ? "owner" : "editor",
  sharing_v2: sharingV2,
});

/** A teammate's level: the derived editors under sharing v2, else the caller's legacy check. */
export const teammateAccess = (doc: Doc, legacyEditor: boolean): "editor" | "viewer" => {
  try {
    if (!sharingV2Doc(doc)) return legacyEditor ? "editor" : "viewer";
    const stored = field(doc, "editors");
    const editors = Array.isArray(stored) ? stored : [];
    const firm = firmOf(doc);
    return (ctx.uid && editors.includes(ctx.uid)) || (firm && editors.includes(`team:${firm}`))
      ? "editor"
      : "viewer";
  } catch {
    return "viewer";
  }
};

const sum = (counts: KindCounts | null | undefined) =>
  counts ? Object.values(counts).reduce<number>((n, c) => n + (c ?? 0), 0) : undefined;

export type CreateEntry = "switcher" | "plan_overview" | "deep_link" | "team_page";
let createEntry: CreateEntry | null = null;

export type SwitchTrigger =
  | "switcher"
  | "team_created"
  | "invite_link"
  | "left_team"
  | "project_link"
  | "board_link"
  | "collection_link"
  | "concept_link"
  | "personal_banner"
  | "welcome";
export type WelcomeCta = "shared" | "members" | "got_it" | "close";
export type FileAction = "filed" | "moved" | "unfiled";
/** The wizard's "team is ready" screen, or the Members page checklist. */
export type SetupPlace = "ready" | "checklist";

export const teamAnalytics = {
  createStarted: (entry: CreateEntry) => {
    createEntry = entry;
    fire("Create Started", "team_create_started", { entry });
  },
  created: (p: {
    firmId: string;
    seats: number;
    interval: string;
    hasDomain: boolean;
    currency?: string | null;
  }) =>
    fire("Created", "team_created", {
      firm_id: p.firmId,
      seats: p.seats,
      interval: p.interval,
      has_domain: p.hasDomain,
      currency: p.currency,
      entry: createEntry,
    }),
  seatsChanged: (p: {
    firmId: string;
    teamRole: string | null;
    before: number;
    after: number;
    used: number;
    entry: "members" | "billing";
  }) =>
    fire("Seats Changed", "team_seats_changed", {
      firm_id: p.firmId,
      team_role: p.teamRole,
      seats_before: p.before,
      seats_after: p.after,
      used_seats: p.used,
      entry: p.entry,
    }),
  inviteSent: (p: {
    firmId: string;
    teamRole: string | null;
    inviteRole: string;
    reinvite: boolean;
    seatsLeft: number;
  }) =>
    fire("Invite Sent", "team_invite_sent", {
      firm_id: p.firmId,
      team_role: p.teamRole,
      invite_role: p.inviteRole,
      reinvite: p.reinvite,
      seats_left: p.seatsLeft,
    }),
  inviteResent: (p: { firmId: string; teamRole: string | null; inviteRole: string; daysPending: number | null }) =>
    fire("Invite Resent", "team_invite_resent", {
      firm_id: p.firmId,
      team_role: p.teamRole,
      invite_role: p.inviteRole,
      days_pending: p.daysPending,
    }),
  inviteCancelled: (p: { firmId: string; teamRole: string | null; inviteRole: string; daysPending: number | null }) =>
    fire("Invite Cancelled", "team_invite_cancelled", {
      firm_id: p.firmId,
      team_role: p.teamRole,
      invite_role: p.inviteRole,
      days_pending: p.daysPending,
    }),
  joined: (p: { uid: string | null; firmId: string; newAccount: boolean }) => {
    if (firstJoin(p.uid, p.firmId))
      fire("Joined", "team_joined", { firm_id: p.firmId, new_account: p.newAccount });
  },
  /** Once per team and place per tab. */
  setupViewed: (p: { firmId: string; place: SetupPlace; stepsDone?: number; steps?: number }) => {
    if (onceForItem(`setup:${p.place}:${p.firmId}`))
      fire("Setup Viewed", "team_setup_viewed", {
        firm_id: p.firmId,
        place: p.place,
        steps_done: p.stepsDone,
        steps: p.steps,
      });
  },
  setupStepStart: (p: { firmId: string; step: string; place: SetupPlace }) =>
    fire("Setup Step Start", "team_setup_step_start", { firm_id: p.firmId, step: p.step, place: p.place }),
  setupDismissed: (p: { firmId: string; stepsDone: number; steps: number }) =>
    fire("Setup Dismissed", "team_setup_dismissed", { firm_id: p.firmId, steps_done: p.stepsDone, steps: p.steps }),
  setupCompleted: (p: { firmId: string; steps: number }) =>
    fire("Setup Completed", "team_setup_completed", { firm_id: p.firmId, steps: p.steps }),
  welcomeViewed: (p: { firmId: string; teamRole: string; inTeam: boolean; owner: boolean }) =>
    fire("Welcome Viewed", "team_welcome_viewed", {
      firm_id: p.firmId,
      team_role: p.teamRole,
      in_team: p.inTeam,
      owner: p.owner,
    }),
  welcomeClosed: (p: { firmId: string; teamRole: string; cta: WelcomeCta }) =>
    fire("Welcome Closed", "team_welcome_closed", { firm_id: p.firmId, team_role: p.teamRole, cta: p.cta }),
  shareTipViewed: (p: { firmId: string; itemKind: AccessKind }) =>
    fire("Share Tip Viewed", "team_share_tip_viewed", { firm_id: p.firmId, item_kind: p.itemKind }),
  shareTipClosed: (p: { firmId: string; itemKind: AccessKind; cta: "share" | "got_it" | "away" }) =>
    fire("Share Tip Closed", "team_share_tip_closed", { firm_id: p.firmId, item_kind: p.itemKind, cta: p.cta }),
  memberRemoved: (p: {
    firmId: string;
    teamRole: string | null;
    memberRole: string | null;
    selfLeave: boolean;
    sharingV2: boolean;
    removal?: { toPersonal?: KindCounts; transfer?: KindCounts; grants?: KindCounts } | null;
  }) =>
    fire("Member Removed", "team_member_removed", {
      firm_id: p.firmId,
      team_role: p.teamRole,
      member_role: p.memberRole,
      self_leave: p.selfLeave,
      sharing_v2: p.sharingV2,
      items_to_personal: sum(p.removal?.toPersonal),
      items_kept: sum(p.removal?.transfer),
      grants_removed: sum(p.removal?.grants),
    }),
  memberStatusChanged: (p: { firmId: string; teamRole: string | null; memberRole: string; active: boolean }) =>
    fire("Member Status Changed", "team_member_status_changed", {
      firm_id: p.firmId,
      team_role: p.teamRole,
      member_role: p.memberRole,
      active: p.active,
    }),
  workspaceSwitched: (p: {
    fromFirmId?: string | null;
    toFirmId: string | null;
    trigger: SwitchTrigger;
  }) => {
    const from = p.fromFirmId === undefined ? activeFirm : p.fromFirmId;
    fire("Workspace Switched", "team_workspace_switched", {
      from_kind: from ? "team" : "personal",
      to_kind: p.toFirmId ? "team" : "personal",
      firm_id: p.toFirmId ?? from,
      trigger: p.trigger,
    });
  },
  shareDialogOpened: (item: TeamItem) =>
    fire("Share Dialog Opened", "team_share_dialog_opened", { ...item }),
  itemShared: (item: TeamItem, p: { level: Level; peopleCount: number; hadInherited: boolean }) =>
    fire("Item Shared", "team_item_shared", {
      ...item,
      audience: "person",
      share_level: p.level,
      people_count: p.peopleCount,
      had_inherited: p.hadInherited,
    }),
  shareLevelChanged: (item: TeamItem, p: { level: Level; prevLevel: Level }) =>
    fire("Share Level Changed", "team_share_level_changed", {
      ...item,
      audience: "person",
      share_level: p.level,
      prev_level: p.prevLevel,
    }),
  accessRemoved: (item: TeamItem, p: { prevLevel: Level }) =>
    fire("Access Removed", "team_access_removed", {
      ...item,
      audience: "person",
      prev_level: p.prevLevel,
    }),
  everyoneChanged: (
    item: TeamItem,
    p: { level: EveryoneLevel; prevLevel: EveryoneLevel; childrenRebuilt?: number },
  ) =>
    fire("Everyone Changed", "team_everyone_changed", {
      ...item,
      audience: "everyone",
      share_level: p.level,
      prev_level: p.prevLevel,
      children_rebuilt: p.childrenRebuilt,
    }),
  linkCopied: (item: TeamItem, p: { linkKind: "view" | "read_only" | "share_page" | "template"; madePublic?: boolean }) =>
    fire("Link Copied", "team_link_copied", { ...item, link_kind: p.linkKind, made_public: p.madePublic }),
  publicChanged: (item: TeamItem, p: { isPublic: boolean }) =>
    fire("Public Changed", "team_public_changed", { ...item, is_public: p.isPublic }),
  /** Once per item per tab, and only for a teammate's team item. */
  itemOpened: (item: TeamItem | null) => {
    if (item && onceForItem(`opened:${item.item_kind}:${item.item_id}`))
      fire("Item Opened", "team_item_opened", { ...item });
  },
  /** Once per item per tab, and only for a teammate's team item. */
  itemEdited: (item: TeamItem | null) => {
    if (item && onceForItem(`edited:${item.item_kind}:${item.item_id}`))
      fire("Item Edited", "team_item_edited", { ...item });
  },
  itemFiled: (
    item: TeamItem,
    p: { action: FileAction; projectId: string | null; projectShared?: boolean | null; bulkCount?: number },
  ) =>
    fire("Item Filed", "team_item_filed", {
      ...item,
      action: p.action,
      project_id: p.projectId,
      project_shared: p.projectShared,
      bulk_count: p.bulkCount,
    }),
  projectDeleted: (p: {
    firmId: string | null;
    sharingV2: boolean;
    archive?: KindCounts | null;
    unfile?: KindCounts | null;
  }) =>
    fire("Project Deleted", "team_project_deleted", {
      firm_id: p.firmId,
      sharing_v2: p.sharingV2,
      archived_boards: p.archive?.board ?? (p.archive ? 0 : undefined),
      archived_concepts: p.archive?.concept ?? (p.archive ? 0 : undefined),
      archived_schedules: p.archive?.schedule ?? (p.archive ? 0 : undefined),
      unfiled_items: sum(p.unfile),
    }),
  movedToTeam: (
    item: TeamItem,
    p: { moved?: Array<{ kind: string }>; unfiled?: Array<{ kind: string }> },
  ) =>
    fire("Moved To Team", "team_item_moved_to_team", () => ({
      ...item,
      moved_count: (p.moved ?? []).length,
      unfiled_count: (p.unfiled ?? []).length,
      concepts_moved: (p.moved ?? []).filter((m) => m.kind === "concept").length,
    })),
};

/** filed / moved / unfiled from the old and new project pointers; null = no change. */
export const fileActionOf = (from: string | null | undefined, to: string | null | undefined): FileAction | null => {
  const a = from || null;
  const b = to || null;
  if (a === b) return null;
  return !b ? "unfiled" : a ? "moved" : "filed";
};

/** Whether a project doc shares anything beyond its owner (v2 access list). */
export const projectShared = (project: Doc): boolean | null => {
  const access = field(project, "access") as { everyone?: string; members?: Record<string, string> } | undefined;
  if (!access) return null;
  return (access.everyone ?? "none") !== "none" || Object.keys(access.members ?? {}).length > 0;
};
